// OWNER: AREA-EVENTS (event-types/*, messages/*, requests/*). Components go in src/features/events.
// Message detail: what the customer received, its delivery timeline, and Resend/Cancel.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertCircle, Archive, Eye, Forward, Hourglass, Mail } from 'lucide-react'
import { asApiError } from '@/api/errors'
import { usePermissions } from '@/auth/usePermissions'
import { CodeBlock } from '@/components/CodeBlock'
import { IdText } from '@/components/CopyButton'
import { EmailPreviewFrame } from '@/components/EmailPreviewFrame'
import { EmptyState } from '@/components/EmptyState'
import { JsonViewer } from '@/components/JsonViewer'
import { KeyValueList } from '@/components/KeyValueList'
import { PageHeader } from '@/components/PageHeader'
import { ErrorState } from '@/components/QueryState'
import { RelativeTime } from '@/components/RelativeTime'
import { StatusBadge } from '@/components/StatusBadge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Skeleton, SkeletonText } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { messageQuery } from '@/features/events/api'
import { MessageAttachments } from '@/features/attachments/MessageAttachments'
import { MessageCrumb } from '@/features/events/crumbs'
import { formatAddress, formatAddresses } from '@/features/events/format'
import { MessageActions } from '@/features/events/messages/MessageActions'
import { MessageTimeline } from '@/features/events/messages/MessageTimeline'
import { heldInfo, isShadow, suppressedRecipients, testReason } from '@/features/events/timeline'
import type { MessageDetail } from '@/features/events/types'
import { formatDateTime } from '@/lib/format'
import { ModeBadge } from '@/components/ModeBadge'
import { splitEntityKeys } from '@/features/timelines/model'
import { KeyChip, ShadowBadge } from '@/features/timelines/TimelineView'

const TABS = ['preview', 'text', 'attachments', 'timeline', 'raw', 'payload'] as const
type Tab = (typeof TABS)[number]

export const Route = createFileRoute('/messages/$messageId')({
  staticData: { crumb: MessageCrumb },
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } => ({
    tab: TABS.includes(s.tab as Tab) && s.tab !== 'preview' ? (s.tab as Tab) : undefined,
  }),
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(messageQuery(params.messageId))
  },
  component: MessageDetailPage,
})

/** Poll while the message is still moving through the pipeline. */
const IN_FLIGHT = new Set(['queued', 'sending'])

function MessageDetailPage() {
  const { messageId } = Route.useParams()
  const { tab = 'preview' } = Route.useSearch()
  const navigate = Route.useNavigate()
  const q = useQuery({
    ...messageQuery(messageId),
    refetchInterval: (query) =>
      query.state.data && IN_FLIGHT.has(query.state.data.status) ? 5000 : false,
  })

  if (q.isPending) return <DetailSkeleton />
  if (q.error)
    return asApiError(q.error).isNotFound ? (
      <EmptyState
        icon={Mail}
        title="This message doesn’t exist"
        description="It may have been deleted with its application, or the link is out of date."
        action={
          <Button asChild>
            <Link to="/messages">Back to messages</Link>
          </Button>
        }
      />
    ) : (
      <ErrorState error={q.error} onRetry={() => void q.refetch()} />
    )

  const m = q.data
  return (
    <>
      <PageHeader
        title={m.subject || '(no subject)'}
        meta={
          <>
            <StatusBadge status={m.status} />
            {m.test && <ModeBadge mode="test" />}
            {isShadow(m.events) && <ShadowBadge />}
            {m.status === 'queued' && m.send_at && <Badge tone="info">Scheduled</Badge>}
            <span>
              To{' '}
              <span className="text-ink">
                {formatAddresses(m.to) ||
                  suppressedRecipients(m.events)
                    .map((r) => r.email)
                    .join(', ') ||
                  '—'}
              </span>
            </span>
            <span>
              Created <RelativeTime value={m.created_at} />
            </span>
            <IdText id={m.id} />
          </>
        }
        actions={<MessageActions message={m} />}
      />
      {m.last_error && !['delivered', 'sent'].includes(m.status) && (
        <div
          role="alert"
          className="mb-5 flex gap-2 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-danger" />
          <div className="min-w-0">
            <div className="font-medium text-danger">
              {m.status === 'queued'
                ? `Attempt ${m.attempts} of ${m.max_attempts} failed; retrying`
                : 'Delivery failed'}
            </div>
            <div className="mt-0.5 font-mono text-xs break-words text-ink-2">{m.last_error}</div>
          </div>
        </div>
      )}
      {m.status === 'held' && <HeldNote message={m} />}
      {m.test && <TestRoutingNote message={m} />}
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <Tabs
          value={tab}
          onValueChange={(v) =>
            void navigate({
              search: (p) => ({ ...p, tab: v === 'preview' ? undefined : (v as Tab) }),
              replace: true,
            })
          }
          className="min-w-0"
        >
          <TabsList>
            <TabsTrigger value="preview">Preview</TabsTrigger>
            <TabsTrigger value="text">Text</TabsTrigger>
            <TabsTrigger value="attachments">
              Attachments <span className="tabular text-xs text-ink-3">{m.attachments.length}</span>
            </TabsTrigger>
            <TabsTrigger value="timeline">
              Timeline <span className="tabular text-xs text-ink-3">{m.events.length}</span>
            </TabsTrigger>
            <TabsTrigger value="raw">Headers and raw</TabsTrigger>
            <TabsTrigger value="payload">Payload</TabsTrigger>
          </TabsList>
          <TabsContent value="preview">
            <BodyGate message={m}>
              {m.html_body != null ? (
                <EmailPreviewFrame
                  html={m.html_body}
                  subject={m.subject || '(no subject)'}
                  from={formatAddress({ email: m.from_email, name: m.from_name || null })}
                  to={formatAddresses(m.to)}
                  height="min(72vh, 860px)"
                  title={`Email: ${m.subject}`}
                />
              ) : (
                <EmptyState
                  compact
                  icon={Eye}
                  title="No HTML part"
                  description="This message was sent as plain text only. See the Text tab."
                />
              )}
            </BodyGate>
          </TabsContent>
          <TabsContent value="text">
            <BodyGate message={m}>
              {m.text_body ? (
                <CodeBlock value={m.text_body} language="text" maxHeight="70vh" />
              ) : (
                <EmptyState
                  compact
                  icon={Eye}
                  title="No text part"
                  description="This message has no plain-text body."
                />
              )}
            </BodyGate>
          </TabsContent>
          <TabsContent value="attachments">
            <MessageAttachments messageId={m.id} attachments={m.attachments} />
          </TabsContent>
          <TabsContent value="timeline">
            <Card>
              <CardBody className="pt-4">
                <MessageTimeline events={m.events} />
              </CardBody>
            </Card>
          </TabsContent>
          <TabsContent value="raw" className="space-y-4">
            <div className="space-y-1.5">
              <h3 className="text-sm font-semibold text-ink">Headers</h3>
              {Object.keys(m.headers ?? {}).length ? (
                <CodeBlock value={m.headers} maxHeight={320} />
              ) : (
                <p className="text-sm text-ink-2">No custom headers.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <h3 className="text-sm font-semibold text-ink">Message record</h3>
              <JsonViewer value={withoutBodies(m)} defaultExpandDepth={1} maxHeight={560} />
            </div>
          </TabsContent>
          <TabsContent value="payload">
            <BodyGate message={m} what="payload">
              {m.payload != null ? (
                <JsonViewer value={m.payload} defaultExpandDepth={3} maxHeight="70vh" />
              ) : (
                <EmptyState
                  compact
                  icon={Archive}
                  title="No payload"
                  description={
                    m.request_id
                      ? 'The request’s payload is no longer stored.'
                      : 'This message wasn’t created from a request with a payload.'
                  }
                />
              )}
            </BodyGate>
          </TabsContent>
        </Tabs>

        <div className="space-y-5">
          <Card>
            <CardHeader title="Details" />
            <CardBody>
              <KeyValueList labelWidth="6.5rem" className="text-[13px]" items={detailItems(m)} />
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Delivery" />
            <CardBody>
              <KeyValueList labelWidth="6.5rem" className="text-[13px]" items={deliveryItems(m)} />
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  )
}

/** Hides bodies for users without permission, and explains purged content. */
function BodyGate({
  message,
  what = 'content',
  children,
}: {
  message: MessageDetail
  what?: 'content' | 'payload'
  children: React.ReactNode
}) {
  const { can } = usePermissions()
  if (!can('view_message_bodies'))
    return (
      <EmptyState
        compact
        icon={Eye}
        title={`You can’t view message ${what}`}
        description="Your role can see message metadata but not bodies or payloads."
      />
    )
  if (message.purged_at)
    return (
      <EmptyState
        compact
        icon={Archive}
        title="Content purged"
        description={`The body and payload were removed on ${formatDateTime(message.purged_at)} under the application’s retention policy. Metadata and the timeline are kept.`}
      />
    )
  return <>{children}</>
}

function withoutBodies(m: MessageDetail) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { html_body, text_body, payload, events, attachments, ...rest } = m
  return rest
}

function detailItems(m: MessageDetail) {
  return [
    { label: 'From', value: formatAddress({ email: m.from_email, name: m.from_name || null }) },
    { label: 'To', value: formatAddresses(m.to) },
    {
      label: 'Suppressed',
      hideEmpty: true,
      value: suppressedLabel(m) || undefined,
    },
    { label: 'Cc', value: formatAddresses(m.cc), hideEmpty: true },
    { label: 'Bcc', value: formatAddresses(m.bcc), hideEmpty: true },
    {
      label: 'Reply-to',
      value: m.reply_to ? formatAddress(m.reply_to) : undefined,
      hideEmpty: true,
    },
    {
      label: 'Event',
      hideEmpty: true,
      value: m.event_key ? (
        m.event_type_id ? (
          <Link
            to="/event-types/$eventTypeId"
            params={{ eventTypeId: m.event_type_id }}
            className="font-mono text-xs text-brass hover:underline"
          >
            {m.event_key}
          </Link>
        ) : (
          <span className="font-mono text-xs">{m.event_key}</span>
        )
      ) : undefined,
    },
    { label: 'Rule', value: m.rule_name, hideEmpty: true },
    {
      label: 'Keys',
      hideEmpty: true,
      value: m.entity_keys.length ? (
        <span className="flex flex-wrap gap-1">
          {splitEntityKeys(m.entity_keys).map((k) => (
            <KeyChip key={k.raw} name={k.name} value={k.value} />
          ))}
        </span>
      ) : undefined,
    },
    { label: 'Locale', value: m.locale || undefined, hideEmpty: true },
    {
      label: 'Dedupe key',
      hideEmpty: true,
      value: m.dedupe_key ? (
        <span className="font-mono text-xs break-all">{m.dedupe_key}</span>
      ) : undefined,
    },
    {
      label: 'Template',
      hideEmpty: true,
      value: m.template_key ? (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {m.template_id ? (
            <Link
              to="/templates/$templateId"
              params={{ templateId: m.template_id }}
              className="font-mono text-xs text-brass hover:underline"
            >
              {m.template_key}
            </Link>
          ) : (
            <span className="font-mono text-xs">{m.template_key}</span>
          )}
          {m.template_version != null && <Badge tone="outline">v{m.template_version}</Badge>}
        </span>
      ) : undefined,
    },
    {
      label: 'Request',
      hideEmpty: true,
      value: m.request_id ? (
        <Link
          to="/requests/$requestId"
          params={{ requestId: m.request_id }}
          className="font-mono text-xs break-all text-brass hover:underline"
        >
          {m.request_id}
        </Link>
      ) : undefined,
    },
    {
      label: 'Tags',
      hideEmpty: true,
      value: m.tags.length ? (
        <span className="flex flex-wrap gap-1">
          {m.tags.map((t) => (
            <Badge key={t} tone="outline" className="font-mono">
              {t}
            </Badge>
          ))}
        </span>
      ) : undefined,
    },
    {
      label: 'Metadata',
      hideEmpty: true,
      value: Object.keys(m.metadata ?? {}).length ? (
        <JsonViewer value={m.metadata} defaultExpandDepth={1} maxHeight={200} />
      ) : undefined,
    },
  ]
}

/** "a@x.test (manual), b@x.test (bounce)" for the Details card. */
function suppressedLabel(m: MessageDetail): string {
  return suppressedRecipients(m.events)
    .map((r) => (r.reason ? `${r.email} (${r.reason})` : r.email))
    .join(', ')
}

function deliveryItems(m: MessageDetail) {
  const when = (v: string | null | undefined) => (v ? <RelativeTime value={v} /> : undefined)
  return [
    { label: 'Provider', value: m.provider },
    {
      label: 'Provider ID',
      value: m.provider_message_id ? (
        <IdText id={m.provider_message_id} className="max-w-full" />
      ) : undefined,
      hideEmpty: true,
    },
    { label: 'Attempts', value: `${m.attempts} of ${m.max_attempts}` },
    {
      // A cancelled message keeps send_at, but it will never go out then, so
      // show the absolute time rather than a countdown.
      label:
        m.status === 'cancelled'
          ? 'Was scheduled'
          : m.status === 'held'
            ? 'Held until'
            : 'Scheduled',
      value: !m.send_at ? undefined : m.status === 'cancelled' ? (
        formatDateTime(m.send_at)
      ) : (
        <span>
          <RelativeTime value={m.send_at} />
          <span className="block text-xs text-ink-3">{formatDateTime(m.send_at)}</span>
        </span>
      ),
      hideEmpty: true,
    },
    { label: 'Sent', value: when(m.sent_at), hideEmpty: true },
    { label: 'Delivered', value: when(m.delivered_at), hideEmpty: true },
    { label: 'Opened', value: when(m.opened_at), hideEmpty: true },
    { label: 'Clicked', value: when(m.clicked_at), hideEmpty: true },
    {
      label: m.purged_at ? 'Purged' : 'Purge after',
      value: m.purged_at ? formatDateTime(m.purged_at) : formatDateTime(m.expires_at),
    },
  ]
}

function DetailSkeleton() {
  return (
    <div aria-busy>
      <Skeleton className="h-7 w-96 max-w-full" />
      <Skeleton className="mt-3 h-4 w-80 max-w-full" />
      <div className="mt-8 grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <Skeleton className="h-[480px] w-full" />
        <div className="space-y-3">
          <SkeletonText lines={6} />
        </div>
      </div>
    </div>
  )
}

/** Why a send policy is holding this message, and how to stop it. */
function HeldNote({ message: m }: { message: MessageDetail }) {
  const info = heldInfo(m.events)
  const keys = splitEntityKeys(m.entity_keys)
  const tone = 'var(--st-held)'
  let text = 'A send policy is holding this message.'
  if (info && 'until' in info)
    text = `A send policy is holding this message until ${formatDateTime(info.until)}.`
  else if (info?.waitingFor)
    text = `Waiting for ${info.waitingFor.event} to be ${
      info.waitingFor.until === 'sent' ? 'sent' : 'received'
    } for the same key${info.deadline ? `, until ${formatDateTime(info.deadline)}` : ''}. ${
      info.waitingFor.on_timeout === 'drop'
        ? 'If it doesn’t arrive by then, this message is dropped.'
        : 'If it doesn’t arrive by then, this message sends anyway.'
    }`
  return (
    <div
      className="mb-5 flex gap-2 rounded-md border px-3 py-2 text-sm"
      style={{
        borderColor: `color-mix(in oklab, ${tone} 30%, transparent)`,
        background: `color-mix(in oklab, ${tone} 7%, var(--surface))`,
      }}
    >
      <Hourglass className="mt-0.5 size-4 shrink-0" style={{ color: tone }} />
      <div className="min-w-0 text-ink-2">
        <span className="font-medium text-ink">{text}</span> A later event can cancel it, and you
        can cancel it yourself.{' '}
        {keys[0] && (
          <Link
            to="/timelines"
            search={{ key: keys[0].raw }}
            className="text-brass hover:underline"
          >
            See the timeline
          </Link>
        )}
      </div>
    </div>
  )
}

/**
 * How test mode handled this test message: captured (no real recipient) or
 * redirected to test inboxes. The timeline has the per-recipient detail.
 */
function TestRoutingNote({ message: m }: { message: MessageDetail }) {
  const captured = m.events.find((e) => e.type === 'captured')
  const redirected = m.events.find((e) => e.type === 'redirected')
  if (m.status !== 'captured' && !redirected) return null
  const reason = testReason(
    (captured?.detail as { reason?: string } | undefined)?.reason ?? undefined,
  )
  const inboxes = (redirected?.detail as { redirect_to?: unknown } | undefined)?.redirect_to
  const Icon = m.status === 'captured' ? Archive : Forward
  // The captured status color, or the redirect tone the timeline uses.
  const tone = m.status === 'captured' ? 'var(--st-captured)' : 'var(--brass)'
  return (
    <div
      className="mb-5 flex gap-2 rounded-md border px-3 py-2 text-sm"
      style={{
        borderColor: `color-mix(in oklab, ${tone} 30%, transparent)`,
        background: `color-mix(in oklab, ${tone} 7%, var(--surface))`,
      }}
    >
      <Icon className="mt-0.5 size-4 shrink-0" style={{ color: tone }} />
      <div className="min-w-0 text-ink-2">
        <span className="font-medium text-ink">
          {m.status === 'captured'
            ? 'Captured: this test message wasn’t delivered to anyone.'
            : `Redirected: a copy for recipients not on the allowlist went to ${
                Array.isArray(inboxes) ? inboxes.join(', ') : 'the test inboxes'
              }.`}
        </span>{' '}
        {m.status === 'captured' && reason}{' '}
        <Link
          to="/applications/$appId/settings"
          params={{ appId: m.application_id }}
          hash="test-mode"
          className="text-brass hover:underline"
        >
          Test mode settings
        </Link>
      </div>
    </div>
  )
}
